import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LoadingSpinnerComponent } from '../shared/loading-spinner/loading-spinner.component';

export interface OtpData {
  otpCode: string[];
  hasError: boolean;
  errorMessage: string;
  countdown: number;
}

@Component({
  selector: 'app-otp-block',
  imports: [CommonModule, FormsModule, LoadingSpinnerComponent],
  templateUrl: './otp-block.component.html',
  standalone: true,
})
export class OtpBlockComponent implements OnInit, OnDestroy, OnChanges {
  @Input() length: number = 4;
  @Input() valueDisplay: string = '';
  @Input() instructionText: string = '';
  @Input() isLoading: boolean = false;

  @Input() submitLabel: string = 'VERIFICAR CÓDIGO';
  @Input() bottomText: string = '';
  @Input() withResendButton: boolean = true;
  @Input() resendTimer: number = 60;

  @Input() initialData?: Partial<OtpData>;

  @Output() onOtpComplete = new EventEmitter<string>();
  @Output() onResendCode = new EventEmitter<void>();
  @Output() onSubmit = new EventEmitter<string>();

  otpCode: string[] = [];

  otpData: OtpData = {
    otpCode: [],
    hasError: false,
    errorMessage: '',
    countdown: 0,
  };

  private countdownTimer?: any;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.initializeOtpData();
    if (this.resendTimer && this.resendTimer > 0) {
      this.startCountdown();
    }
  }

  ngOnDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isLoading']) {
      console.log('isLoading', changes['isLoading'].currentValue);
      this.isLoading = changes['isLoading'].currentValue;
      this.cdr.detectChanges();
    }
  }

  private initializeOtpData(): void {
    this.otpData.otpCode = new Array(this.length).fill('');

    if (this.initialData) {
      this.otpData = { ...this.otpData, ...this.initialData };
    }
  }

  onOtpInput(event: any, index: number): void {
    const value = event.target.value;

    if (this.isValidDigit(value)) {
      this.handleValidOtpInput(value, index, event);
    } else {
      this.otpData.otpCode[index] = '';
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    if (
      event.key === 'Backspace' &&
      !this.otpData.otpCode[index] &&
      index > 0
    ) {
      const prevInput = (event.target as HTMLElement).parentElement?.children[
        index - 1
      ] as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
        this.otpData.otpCode[index - 1] = '';
      }
      return;
    }

    if (event.key === 'Enter' && this.isOtpComplete) {
      this.verify();
      return;
    }
  }

  verify(): void {
    const code = this.getOtpCode();
    if (this.isOtpComplete) {
      this.onSubmit.emit(code);
    }
  }

  resendCode(): void {
    if (this.canResendCode) {
      this.resetOtpInputs();
      this.startCountdown();
      this.onResendCode.emit();
    }
  }

  resetOtpInputs(): void {
    this.otpData.otpCode = new Array(this.length).fill('');
  }

  private startCountdown(): void {
    if (!this.resendTimer) return;

    this.otpData.countdown = this.resendTimer;
    this.countdownTimer = setInterval(() => {
      this.otpData.countdown--;
      if (this.otpData.countdown <= 0) {
        clearInterval(this.countdownTimer);
      }
      this.cdr.detectChanges();
    }, 1000);
  }

  private isValidDigit(value: string): boolean {
    return !!value && /^\d$/.test(value);
  }

  private handleValidOtpInput(value: string, index: number, event: any): void {
    this.otpData.otpCode[index] = value;

    // Move to next input
    if (index < this.length - 1) {
      const nextInput = event.target.parentElement.children[index + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }

    // Auto-verify when complete
    if (this.isOtpComplete) {
      this.onOtpComplete.emit(this.getOtpCode());
    }
  }

  private getOtpCode(): string {
    return this.otpData.otpCode.join('');
  }

  // Getters
  get isOtpComplete(): boolean {
    return this.otpData.otpCode.every((digit) => digit !== '');
  }

  get canResendCode(): boolean {
    return this.otpData.countdown <= 0;
  }

  get isButtonDisabled(): boolean {
    return !this.isOtpComplete || this.isLoading;
  }

  // Template helper methods
  trackByIndex(index: number): number {
    return index;
  }

  getOtpInputClass(hasError: boolean, digit: string): string {
    if (hasError) {
      return 'otp-input w-10 h-10 text-center text-sm font-semibold border-2 border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-red-50';
    }
    if (digit !== '') {
      return 'otp-input w-10 h-10 text-center text-sm font-semibold border-2 border-appPrimary-500 focus:outline-none focus:ring-2 focus:ring-appPrimary-500 focus:border-transparent';
    }
    return 'otp-input w-10 h-10 text-center text-sm font-semibold border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-appPrimary-500 focus:border-transparent';
  }

  getButtonClass(): string {
    const baseClasses =
      'w-full py-2.5 rounded-lg font-medium text-base transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';

    if (this.isButtonDisabled) {
      return `${baseClasses} bg-gray-300 text-gray-500 cursor-not-allowed`;
    }

    return `${baseClasses} bg-appPrimary-500 text-white hover:bg-appPrimary-600 focus:ring-appPrimary-500`;
  }
}
